import { AutocompleteRenderGroupParams, ListSubheader, Typography } from '@mui/joy';
import { useCallback } from 'react';
import { Fragment } from 'react';
import { Trans } from 'react-i18next';

import { TextEllipsis } from 'core/elements';
import { Color, Level, Variant } from 'theme/enums';

interface IProps {
  contactGroupName: string;
  mainGroupI18nKey: string;
  otherGroupI18nKey: string;
}

export const useRenderGroup = ({
  contactGroupName,
  mainGroupI18nKey,
  otherGroupI18nKey,
}: IProps) => {
  return useCallback(
    (params: AutocompleteRenderGroupParams) => {
      if (params.group === 'new') {
        return <Fragment key={params.key}>{params.children}</Fragment>;
      }

      return (
        <Fragment key={params.key}>
          <ListSubheader
            sticky
            sx={{ minBlockSize: 'unset', letterSpacing: '0', textTransform: 'none', zIndex: 2 }}
          >
            {params.group === 'main' && (
              <Typography
                level={Level.TITLE_LG}
                color={Color.PRIMARY}
                variant={Variant.SOFT}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 0.5,
                  margin: '-8px -8px 0',
                  padding: '8px',
                  width: 'calc(100% + 16px)',
                }}
              >
                <Trans
                  i18nKey={mainGroupI18nKey}
                  values={{ contactGroupName }}
                  components={{
                    contactGroupWrapper: (
                      <TextEllipsis
                        level={Level.TITLE_LG}
                        color={Color.PRIMARY}
                        lineHeight={32}
                        sx={{
                          borderRadius: '3px',
                          backgroundColor: 'neutral.300',
                          padding: '0 8px',
                        }}
                      />
                    ),
                  }}
                />
              </Typography>
            )}

            {params.group === 'other' && (
              <Typography
                level={Level.TITLE_LG}
                color={Color.WARNING}
                variant={Variant.SOFT}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  backgroundColor: (theme) => theme.palette.warning[200],
                  margin: '-8px -8px 0',
                  padding: '8px',
                  width: 'calc(100% + 16px)',
                  minHeight: '74px',
                }}
              >
                <Trans i18nKey={otherGroupI18nKey} />
              </Typography>
            )}
          </ListSubheader>
          {params.children}
        </Fragment>
      );
    },
    [contactGroupName, mainGroupI18nKey, otherGroupI18nKey]
  );
};
